"use client";
import { useState, useRef } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Check, X, Rocket, Building2, Users, ShieldCheck } from "lucide-react";
// import { useBooking } from "../context/BookingContext";
// import PageHeader from "../components/PageHeader";

// Import AI solution images
import voiceImg from "@/public/images/services/ai.png";
import marketImg from "@/public/images/services/ai.png";
import dataImg from "@/public/images/services/ai.png";

// Import AI capability images
import workflowImg from "@/public/images/services/ai.png";
import dataCapImg from "@/public/images/services/ai.png";
import assistImg from "@/public/images/services/ai.png";
import predictImg from "@/public/images/services/ai.png";

// Import tech stack background
import techstackBg from "@/public/images/services/ai.png";
import PageHeader from "../Components/service/pageHeader";
import Image from "next/image";

const AISolutions = () => {
//   const { openBookingModal, openContactModal } = useBooking();
const customEasing: [number, number, number, number] = [
    0.22,
    1,
    0.36,
    1,
];

  // AI Decision Framework state
  const [activeQuestion, setActiveQuestion] = useState(0);

  // Build steps state
  const [focusedStep, setFocusedStep] = useState<number | null>(null);

  // Tech stack filter state
  const [activeTechFilter, setActiveTechFilter] = useState("All");

  // AI Decision Framework Questions
  const aiDecisionQuestions = [
    {
      index: "01",
      question: "Is AI actually needed here?",
      thinking: "Not every problem needs AI. We validate whether automation, rules, or system design can solve the problem more reliably before introducing AI complexity.",
      prevents: [
        "Unnecessary complexity",
        "Fragile AI dependencies",
        "Over-engineered solutions"
      ],
      enables: [
        "Simpler systems",
        "Faster delivery",
        "Lower long-term risk"
      ]
    },
    {
      index: "02",
      question: "Is the data ready?",
      thinking: "AI systems depend on clean, well-structured data pipelines. We design data foundations before models to ensure consistent, reliable outputs.",
      prevents: [
        "Inconsistent outputs",
        "Hidden data quality issues",
        "Unpredictable AI behavior"
      ],
      enables: [
        "Predictable AI results",
        "Easier monitoring",
        "Faster debugging"
      ]
    },
    {
      index: "03",
      question: "Where does AI fit in the system?",
      thinking: "AI must be embedded into workflows, not bolted on as a feature. We map exactly where AI adds value in your existing processes.",
      prevents: [
        "Isolated AI tools",
        "Poor user adoption",
        "Integration headaches"
      ],
      enables: [
        "Seamless workflow integration",
        "Real operational impact",
        "Natural user experience"
      ]
    },
    {
      index: "04",
      question: "How will this scale and be maintained?",
      thinking: "AI systems require ongoing monitoring, retraining, and optimization. We design for maintainability from day one.",
      prevents: [
        "Model drift issues",
        "Unmonitored failures",
        "Knowledge silos"
      ],
      enables: [
        "Sustainable AI operations",
        "Clear ownership model",
        "Continuous improvement"
      ]
    }
  ];

  // AI Capabilities with images
  const capabilitiesData = [
    {
      id: 1,
      title: "AI Workflow Automation",
      description: "Automate repetitive, rule-based tasks with AI that understands context and adapts to exceptions.",
      image: workflowImg,
      includes: [
        "Document processing and data extraction",
        "Email and communication triage",
        "Approval workflows with intelligent routing",
        "Data validation and enrichment"
      ],
      outcomes: [
        "Reduce manual processing time by 60-80%",
        "Improve accuracy and consistency",
        "Free teams for higher-value work"
      ]
    },
    {
      id: 2,
      title: "Intelligent Data Systems",
      description: "Turn unstructured data into actionable insights with AI-powered analysis and reporting.",
      image: dataCapImg,
      includes: [
        "Natural language querying of business data",
        "Automated report generation",
        "Trend detection and anomaly alerts",
        "Knowledge base creation from documents"
      ],
      outcomes: [
        "Faster access to business insights",
        "Reduced dependency on manual analysis",
        "Better decision-making with real-time data"
      ]
    },
    {
      id: 3,
      title: "AI Assistants",
      description: "Build AI assistants that help your team work faster by answering questions and surfacing relevant information.",
      image: assistImg,
      includes: [
        "RAG-based Q&A systems",
        "Internal knowledge chatbots",
        "Voice-enabled interfaces",
        "Context-aware search and retrieval"
      ],
      outcomes: [
        "Reduce time spent searching for information",
        "Improve onboarding and training efficiency",
        "Scale institutional knowledge access"
      ]
    },
    {
      id: 4,
      title: "Predictive Systems",
      description: "Use AI to predict outcomes, recommend actions, and optimize decisions based on historical data.",
      image: predictImg,
      includes: [
        "Demand and inventory forecasting",
        "Customer behavior prediction",
        "Personalized recommendations",
        "Risk scoring and prioritization"
      ],
      outcomes: [
        "Improve forecast accuracy",
        "Increase conversion and engagement",
        "Reduce waste and optimize resources"
      ]
    }
  ];

  // Card stack state
  const [capabilityCards, setCapabilityCards] = useState(capabilitiesData);

  const moveCardToEnd = (index: any) => {
    setCapabilityCards(prev => [...prev.slice(index + 1), ...prev.slice(0, index + 1)]);
  };

  // Card stack configuration
  const cardOffset = 12;
  const cardScaleStep = 0.05;
  const cardDimStep = 0.12;

  // Real AI Systems
  const realSystems = [
    {
      name: "Voice-Interactive AI Platform",
      summary: "Production voice AI with speech-to-speech document interaction",
      image: voiceImg,
      impact: [
        "90%+ speech recognition accuracy",
        "60% faster document comprehension",
        "Multilingual support across markets"
      ],
      caseStudyLink: "/case-studies"
    },
    {
      name: "Marketplace Intelligence Engine",
      summary: "Analytics platform transforming API data into competitive insights",
      image: marketImg,
      impact: [
        "95% improvement in data accessibility",
        "70% reduction in report generation time",
        "Real-time competitive intelligence"
      ],
      caseStudyLink: "/case-studies"
    },
    {
      name: "Data Intelligence Platform",
      summary: "Adaptive scraping system delivering stable structured data",
      image: dataImg,
      impact: [
        "100% uptime despite UI changes",
        "90% improvement in data stability",
        "Automated data quality assurance"
      ],
      caseStudyLink: "/case-studies"
    }
  ];

  // Build steps
  const buildSteps = [
    {
      number: "01",
      title: "Use Case Analysis",
      description: "We assess where AI creates real value versus where traditional engineering works better. No AI for AI's sake."
    },
    {
      number: "02",
      title: "Data Readiness",
      description: "We evaluate your data quality, availability, and system architecture to ensure AI can be integrated effectively."
    },
    {
      number: "03",
      title: "Model Selection",
      description: "We select the right models and approaches based on your specific requirements, not industry trends."
    },
    {
      number: "04",
      title: "System Integration",
      description: "AI is built as part of your larger system architecture, not as an isolated feature that breaks under load."
    },
    {
      number: "05",
      title: "Validation & Monitoring",
      description: "We implement monitoring, feedback loops, and continuous improvement processes for long-term reliability."
    }
  ];

  // Tech stack with categories
  const techStack = {
    "All": null, // Special case - shows all grouped
    "LLMs": [
      "OpenAI GPT-4 & GPT-4o",
      "Claude & Anthropic APIs",
      "Open-source models (Llama, Mistral)",
      "Fine-tuned domain models"
    ],
    "Data": [
      "Vector databases (Pinecone, Weaviate)",
      "PostgreSQL & TimescaleDB",
      "Redis for caching",
      "Data pipelines (Airflow, Prefect)"
    ],
    "Infra": [
      "AWS & GCP infrastructure",
      "Kubernetes orchestration",
      "Docker containerization",
      "CI/CD automation"
    ],
    "Integrations": [
      "REST & GraphQL APIs",
      "Webhook integrations",
      "OAuth & authentication",
      "Third-party connectors"
    ]
  };

  // Group labels for display

  const techGroupLabels = {
    "LLMs": "LLM Layer",
    "Data": "Data + Storage",
    "Infra": "Infra + Delivery",
    "Integrations": "APIs + Integration"
  };

  return (
    <div className="bg-[#FAFAFA]">
      {/* Page Header */}
      <PageHeader
        label="AI Solutions"
        title="AI Solutions Built for Real Business Impact"
        description="Practical AI systems designed to reduce manual work and scale operations."
      />

      {/* Our Approach - AI Decision Framework */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.26 }}
            className="mb-10"
          >
            <h2 className="text-[28px] sm:text-[34px] text-[#0F172A] mb-2" style={{ fontWeight: 500, lineHeight: 1.15, letterSpacing: "-0.015em" }}>
              Our Approach to AI
            </h2>
            <p className="text-[16px] text-[#64748B]" style={{ fontWeight: 400 }}>
              AI that fits your business, not the other way around.
            </p>
          </motion.div>

          <div className="lg:flex lg:gap-10">
            {/* Left - Decision Questions */}
            <div className="lg:w-[38%] mb-8 lg:mb-0">
              {/* Progress indicator */}
              <div className="flex items-center gap-2 mb-6">
                <span className="text-[12px] text-[#94A3B8]" style={{ fontWeight: 500 }}>
                  {aiDecisionQuestions[activeQuestion].index}
                </span>
                <span className="text-[12px] text-[#CBD5E1]">/</span>
                <span className="text-[12px] text-[#CBD5E1]">04</span>
              </div>

              {/* Question List */}
              <div className="space-y-2">
                {aiDecisionQuestions.map((item, idx) => (
                  <motion.button
                    key={idx}
                    onClick={() => setActiveQuestion(idx)}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.26, delay: idx * 0.05 }}
                    className={`w-full text-left p-4 rounded-lg transition-all duration-150 ${
                      activeQuestion === idx
                        ? "bg-[#FAFAFA] border border-[#E5E7EB]"
                        : "bg-transparent hover:bg-[#FAFAFA]/50"
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <span
                        className={`text-[20px] transition-colors duration-150 ${
                          activeQuestion === idx ? "text-[#7A6FF0]" : "text-[#E5E7EB]"
                        }`}
                        style={{ fontWeight: 600, lineHeight: 1 }}
                      >
                        {item.index}
                      </span>
                      <span
                        className={`text-[15px] transition-all duration-150 ${
                          activeQuestion === idx ? "text-[#0F172A]" : "text-[#94A3B8]"
                        }`}
                        style={{ fontWeight: activeQuestion === idx ? 500 : 400 }}
                      >
                        {item.question}
                      </span>
                    </div>
                  </motion.button>
                ))}
              </div>
            </div>

            {/* Right - Context Panel */}
            <div className="lg:w-[62%]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeQuestion}
                  initial={{ opacity: 0, x: 15 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -15 }}
                  transition={{ duration: 0.18, ease: customEasing }}
                  className="bg-[#FAFAFA] rounded-xl p-6 lg:p-8 border border-[#E5E7EB]"
                >
                  {/* Our Thinking */}
                  <div className="mb-6">
                    <p className="text-[11px] uppercase tracking-wide text-[#94A3B8] mb-3" style={{ fontWeight: 500 }}>
                      Our thinking
                    </p>
                    <p className="text-[15px] text-[#334155]" style={{ lineHeight: 1.6 }}>
                      {aiDecisionQuestions[activeQuestion].thinking}
                    </p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    {/* What This Prevents */}
                    {/* <div>
                      <p className="text-[11px] uppercase tracking-wide text-[#94A3B8] mb-3" style={{ fontWeight: 500 }}>
                        What this prevents
                      </p>
                      <ul className="space-y-2">
                        {aiDecisionQuestions[activeQuestion].prevents.map((item, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <X className="w-4 h-4 text-[#DC2626] mt-0.5 flex-shrink-0" />
                            <span className="text-[14px] text-[#64748B]">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div> */}

                    {/* What This Enables */}
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-[#94A3B8] mb-3" style={{ fontWeight: 500 }}>
                        What this enables
                      </p>
                      <ul className="space-y-2">
                        {aiDecisionQuestions[activeQuestion].enables.map((item, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <Check className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" />
                            <span className="text-[14px] text-[#334155]" style={{ fontWeight: 500 }}>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

      {/* AI Capabilities - Card Stack */}
      <section className="py-16 bg-[#FAFAFA]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.32 }}
            className="mb-12 text-center"
          >
            <h2 className="text-[28px] sm:text-[34px] text-[#0F172A] mb-4" style={{ fontWeight: 500, lineHeight: 1.15, letterSpacing: "-0.015em" }}>
              AI Capabilities
            </h2>
            <p className="text-[16px] text-[#64748B] max-w-2xl mx-auto" style={{ fontWeight: 400, lineHeight: 1.6 }}>
              Drag the top card to explore our AI capabilities.
            </p>
          </motion.div>

          {/* Card Stack - Centered */}
          <div className="flex flex-col items-center">
            <div className="relative w-full max-w-[900px] h-[480px] sm:h-[420px] md:h-[380px]">
              <ul className="relative w-full h-full m-0 p-0 list-none">
                {capabilityCards.map((card, i) => {
                  const isFront = i === 0;
                  const brightness = Math.max(0.4, 1 - i * cardDimStep);
                  const baseZ = capabilityCards.length - i;

                  return (
                    <motion.li
                      key={card.id}
                      className="absolute inset-x-0 mx-auto w-[95%] sm:w-[90%] h-full overflow-hidden rounded-2xl"
                      style={{
                        cursor: isFront ? 'grab' : 'auto',
                        touchAction: 'none',
                        backgroundImage: `url(${card.image})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        boxShadow: isFront
                          ? '0 20px 40px rgba(0, 0, 0, 0.25)'
                          : '0 10px 20px rgba(0, 0, 0, 0.15)',
                      }}
                      animate={{
                        top: `${i * -cardOffset}px`,
                        scale: 1 - i * cardScaleStep,
                        filter: `brightness(${brightness})`,
                        zIndex: baseZ,
                      }}
                      transition={{
                        type: 'spring',
                        stiffness: 170,
                        damping: 26,
                      }}
                      drag={isFront ? 'y' : false}
                      dragConstraints={{ top: 0, bottom: 0 }}
                      dragMomentum={false}
                      onDragEnd={() => isFront && moveCardToEnd(i)}
                      whileDrag={
                        isFront
                          ? {
                              zIndex: capabilityCards.length + 1,
                              cursor: 'grabbing',
                              scale: 1 + 0.02,
                              rotate: 1,
                            }
                          : {}
                      }
                    >
                      {/* Dark overlay for text readability */}
                      <div className="absolute inset-0 bg-black" />

                      {/* Content - Centered */}
                      <div className="relative h-full p-5 sm:p-6 md:p-8 flex flex-col justify-center text-white">
                        <h3 className="text-[20px] sm:text-[24px] md:text-[28px] mb-2" style={{ fontWeight: 600 }}>
                          {card.title}
                        </h3>
                        <p className="text-[13px] sm:text-[14px] text-white/80 mb-4 max-w-2xl" style={{ lineHeight: 1.5 }}>
                          {card.description}
                        </p>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
                          {/* What We Build */}
                          <div>
                            <p className="text-[10px] sm:text-[11px] uppercase tracking-wide text-white/60 mb-2" style={{ fontWeight: 600 }}>
                              What We Build
                            </p>
                            <ul className="space-y-1">
                              {card.includes.map((item, idx) => (
                                <li key={idx} className="flex items-start gap-1.5">
                                  <span className="w-1.5 h-1.5 rounded-full bg-[#7A6FF0] mt-1.5 flex-shrink-0" />
                                  <span className="text-[12px] sm:text-[13px] text-white/90">{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>

                          {/* Outcomes */}
                          <div>
                            <p className="text-[10px] sm:text-[11px] uppercase tracking-wide text-white/60 mb-2" style={{ fontWeight: 600 }}>
                              Outcomes
                            </p>
                            <ul className="space-y-1">
                              {card.outcomes.map((item, idx) => (
                                <li key={idx} className="flex items-start gap-1.5">
                                  <Check className="w-3.5 h-3.5 text-[#10B981] mt-0.5 flex-shrink-0" />
                                  <span className="text-[12px] sm:text-[13px] text-white" style={{ fontWeight: 500 }}>{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </motion.li>
                  );
                })}
              </ul>
            </div>

            {/* Hint text and indicators */}
            <div className="mt-8 text-center">
              <p className="text-[13px] text-[#94A3B8] mb-4" style={{ fontWeight: 500 }}>
                Drag the card up to see the next capability
              </p>
              <div className="flex justify-center gap-2">
                {capabilitiesData.map((cap) => (
                  <div
                    key={cap.id}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      capabilityCards[0].id === cap.id
                        ? 'w-8 bg-[#7A6FF0]'
                        : 'w-2 bg-[#D1D5DB]'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Real AI Systems - Visible on mobile, hover-reveal on desktop */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.32 }}
            className="mb-12"
          >
            <h2 className="text-[28px] sm:text-[34px] text-[#0F172A] mb-4" style={{ fontWeight: 500, lineHeight: 1.15, letterSpacing: "-0.015em" }}>
              Real AI Systems We've Built
            </h2>
            <p className="text-[16px] text-[#64748B] max-w-2xl" style={{ fontWeight: 400, lineHeight: 1.6 }}>
              Production AI delivering measurable results.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {realSystems.map((system, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.28, delay: idx * 0.08 }}
                className="group relative"
              >
                <a
                  href={system.caseStudyLink}
                  className="block rounded-2xl bg-white border border-[#E5E7EB] md:hover:border-[#7A6FF0]/40 hover:-translate-y-1 hover:shadow-xl transition-all duration-200 h-full overflow-hidden"
                >
                  {/* Image Header */}
                  <div className="relative h-44 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#7A6FF0]/10 to-transparent z-10 pointer-events-none" />
                    <Image
                      src={system.image}
                      alt={system.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    {/* Bottom gradient fade */}
                    <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white to-transparent z-10 pointer-events-none" />
                  </div>

                  {/* Content */}
                  <div className="p-6 pt-3">
                    {/* System Info */}
                    <div className="mb-4">
                      <h3 className="text-[17px] text-[#0F172A] mb-2 md:group-hover:text-[#7A6FF0] transition-colors duration-140" style={{ fontWeight: 500 }}>
                        {system.name}
                      </h3>
                      <p className="text-[14px] text-[#64748B]" style={{ lineHeight: 1.5 }}>
                        {system.summary}
                      </p>
                    </div>

                    {/* Impact Metrics */}
                    <div className="pt-4 border-t border-[#E5E7EB]">
                      <p className="text-[11px] uppercase tracking-wide text-[#94A3B8] mb-2" style={{ fontWeight: 500 }}>
                        Impact
                      </p>
                      <ul className="space-y-1">
                        {system.impact.map((item, i) => (
                          <li key={i} className="text-[13px] text-[#334155]" style={{ fontWeight: 500 }}>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* CTA */}
                    <div className="mt-5 flex items-center text-[13px] text-[#7A6FF0] font-medium">
                      View Case Study
                      <ArrowRight className="ml-2 w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
                    </div>
                  </div>
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How We Build AI - Focus Step Mode */}
      {/* <section className="py-16 bg-[#FAFAFA]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.32 }}
            className="mb-12"
          >
            <h2 className="text-[28px] sm:text-[34px] text-[#0F172A] mb-4" style={{ fontWeight: 500, lineHeight: 1.15, letterSpacing: "-0.015em" }}>
              How We Build AI
            </h2>
            <p className="text-[16px] text-[#64748B] max-w-2xl" style={{ fontWeight: 400, lineHeight: 1.6 }}>
              Click any step to focus. Our process ensures AI that works in production.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
            {buildSteps.map((step, idx) => (
              <motion.button
                key={idx}
                onClick={() => setFocusedStep(focusedStep === idx ? null : idx)}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.28, delay: idx * 0.06 }}
                className={`text-left p-5 rounded-xl border transition-all duration-160 ${
                  focusedStep === null || focusedStep === idx
                    ? "bg-white border-[#E5E7EB] opacity-100"
                    : "bg-white/50 border-transparent opacity-40"
                } ${focusedStep === idx ? "scale-[1.02] shadow-lg border-[#7A6FF0]/30" : ""}`}
              >
                <p
                  className={`text-[24px] mb-2 transition-colors duration-160 ${
                    focusedStep === idx ? "text-[#7A6FF0]" : "text-[#CBD5E1]"
                  }`}
                  style={{ fontWeight: 600 }}
                >
                  {step.number}
                </p>
                <p
                  className={`text-[14px] mb-2 transition-colors duration-160 ${
                    focusedStep === null || focusedStep === idx ? "text-[#0F172A]" : "text-[#94A3B8]"
                  }`}
                  style={{ fontWeight: 500 }}
                >
                  {step.title}
                </p>
                <p
                  className={`text-[13px] transition-all duration-160 ${
                    focusedStep === null || focusedStep === idx ? "text-[#64748B]" : "text-[#CBD5E1]"
                  } ${focusedStep === idx ? "max-h-40" : "max-h-12 overflow-hidden"}`}
                  style={{ lineHeight: 1.5 }}
                >
                  {step.description}
                </p>
              </motion.button>
            ))}
          </div>

       
          {focusedStep !== null && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center mt-6"
            >
              <button
                onClick={() => setFocusedStep(null)}
                className="text-[13px] text-[#64748B] hover:text-[#7A6FF0] transition-colors duration-140"
                style={{ fontWeight: 500 }}
              >
                Show all steps
              </button>
            </motion.div>
          )}
        </div>
      </section> */}

      {/* Technology Stack - Premium Design */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Outer wrapper with background image */}
          <div className="relative overflow-hidden rounded-[28px] border border-black/10">

            {/* Background image with slow drift animation - z-0 */}
            <motion.div
              className="absolute inset-0 z-0"
              animate={{
                x: [0, 15, 0, -15, 0],
                y: [0, -10, 0, 10, 0],
              }}
              transition={{
                duration: 25,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              style={{ scale: 1.08 }}
            >
              <Image
                src={techstackBg}
                alt=""
                className="h-full w-full object-cover object-top"
              />
            </motion.div>

            {/* Readability wash - very light - z-10 */}
            <div className="absolute inset-0 z-10 bg-gradient-to-b from-white/5 via-white/12 to-white/20" />

            {/* Violet glow blob - breathing + drift animation */}
            <motion.div
              className="absolute -top-24 -left-24 z-10 h-80 w-80 rounded-full bg-[#7A6FF0]/30 blur-[100px]"
              animate={{
                opacity: [0.25, 0.35, 0.25],
                x: [0, 20, 0],
                y: [0, 15, 0],
              }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            />

            {/* Teal-blue glow blob - breathing + drift animation */}
            <motion.div
              className="absolute -bottom-32 -right-32 z-10 h-96 w-96 rounded-full bg-[#38BDF8]/25 blur-[120px]"
              animate={{
                opacity: [0.20, 0.32, 0.20],
                x: [0, -25, 0],
                y: [0, -20, 0],
              }}
              transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
            />

            {/* Foreground glass panel - z-20 */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, ease: "easeOut" }}
              className="relative z-20 m-4 sm:m-6 md:m-8 rounded-[22px] bg-white/8 backdrop-blur-sm border border-white/30"
            >
              <div className="p-6 sm:p-8 md:p-10">
                {/* Heading */}
                <div className="mb-8">
                  <h2 className="text-[28px] sm:text-[34px] text-white mb-2 drop-shadow-sm" style={{ fontWeight: 600, lineHeight: 1.15, letterSpacing: "-0.015em" }}>
                    Technology Stack
                  </h2>
                  <p className="text-[16px] text-white/90 max-w-2xl drop-shadow-sm" style={{ fontWeight: 500, lineHeight: 1.6 }}>
                    Production-grade tools we use to ship AI systems — fast, reliable, maintainable.
                  </p>
                </div>

                {/* Tech List - All Grouped */}
                <div className="grid sm:grid-cols-2 gap-6">
                  {Object.keys(techStack).filter(key => key !== "All").map((category, catIdx) => (
                    <motion.div
                      key={category}
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.14, delay: catIdx * 0.05 }}
                    >
                      <p className="text-[11px] uppercase tracking-wider text-white/80 mb-3 drop-shadow-sm" style={{ fontWeight: 600 }}>
                        {techGroupLabels[category as keyof typeof techGroupLabels]}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {techStack[category as keyof typeof techStack]?.map((tech) => (
                          <div
                            key={tech}
                            className="px-3 py-2 bg-white/80 backdrop-blur rounded-lg border border-white/50 hover:bg-white transition-all duration-200"
                          >
                            <p className="text-[13px] text-[#0F172A]" style={{ fontWeight: 600 }}>
                              {tech}
                            </p>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="py-16 bg-[#FAFAFA]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="lg:flex lg:gap-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.32 }}
              className="lg:w-[45%] mb-8 lg:mb-0"
            >
              <h2 className="text-[28px] sm:text-[34px] text-[#0F172A] mb-4" style={{ fontWeight: 500, lineHeight: 1.15, letterSpacing: "-0.015em" }}>
                Who This Is For
              </h2>
              <p className="text-[16px] text-[#64748B]" style={{ fontWeight: 400, lineHeight: 1.6 }}>
                AI solutions for teams ready to move beyond experiments.
              </p>
            </motion.div>

            <div className="lg:w-[55%]">
              <div className="space-y-3">
                {[
                  { text: "SaaS companies scaling operations", icon: Rocket },
                  { text: "Enterprises modernizing internal systems", icon: Building2 },
                  { text: "Teams replacing manual workflows", icon: Users },
                  { text: "Products where AI must work reliably in production", icon: ShieldCheck }
                ].map((item, idx) => {
                  const Icon = item.icon;
                  return (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.28, delay: idx * 0.06 }}
                      className="flex items-center gap-3 py-3"
                      style={{ borderBottom: idx < 3 ? "1px solid #E5E7EB" : "none" }}
                    >
                      <Icon className="w-5 h-5 text-[#7A6FF0] flex-shrink-0" />
                      <p className="text-[15px] text-[#334155]" style={{ fontWeight: 500 }}>
                        {item.text}
                      </p>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why ZySoftec */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.32 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="text-[28px] sm:text-[34px] text-[#0F172A] mb-6" style={{ fontWeight: 500, lineHeight: 1.15, letterSpacing: "-0.015em" }}>
              Why ZySoftec for AI
            </h2>

            <div className="flex flex-wrap justify-center gap-3 mb-8">
              {[
                "Engineering-first approach",
                "No black-box implementations",
                "Clean architecture",
                "Long-term ownership mindset"
              ].map((item, idx) => (
                <motion.span
                  key={idx}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.28, delay: idx * 0.06 }}
                  className="px-4 py-2 bg-[#FAFAFA] rounded-full text-[14px] text-[#475569] border border-[#E5E7EB]"
                  style={{ fontWeight: 500 }}
                >
                  {item}
                </motion.span>
              ))}
            </div>

            <p className="text-[15px] text-[#64748B]" style={{ lineHeight: 1.7 }}>
              We build AI systems that your team can understand, maintain, and extend. No vendor lock-in. No magic boxes.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Global CTA */}
      <section
        className="py-12 sm:py-16 lg:py-20 relative overflow-hidden"
        style={{
          background: `
            radial-gradient(500px 200px at 30% 0%, rgba(242,166,90,0.35), transparent 60%),
            radial-gradient(500px 200px at 70% 100%, rgba(122,111,240,0.35), transparent 60%),
            #0B0B0F
          `
        }}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="lg:flex lg:gap-12 lg:items-start">
            {/* Left Column */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.32 }}
              className="lg:w-[58%] mb-8 lg:mb-0"
            >
              <h2 className="text-2xl sm:text-[28px] md:text-[34px] lg:text-[40px] text-white mb-4" style={{ fontWeight: 500, lineHeight: 1.15, letterSpacing: "-0.02em" }}>
                Ready to Build a System That Scales?
              </h2>
              <p className="text-base sm:text-[17px] text-white/70 mb-4 sm:mb-6" style={{ fontWeight: 400, lineHeight: 1.6 }}>
                Whether you're starting from scratch or improving an existing product, we can help you build software that supports your growth.
              </p>
              <p className="text-sm sm:text-[15px] text-white/60" style={{ fontWeight: 400, lineHeight: 1.7 }}>
                Every engagement starts with a conversation. We'll discuss your goals, constraints, and what success looks like for your project.
              </p>
            </motion.div>

            {/* Right Column - CTA Panel */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.32, delay: 0.1 }}
              className="lg:w-[42%]"
            >
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm p-6 sm:p-8">
                <p className="text-[11px] sm:text-[12px] uppercase tracking-wide text-[#7A6FF0] mb-4 sm:mb-6 text-center" style={{ fontWeight: 500 }}>
                  Next Step
                </p>
                <p className="text-sm sm:text-[14px] text-white/70 mb-4 sm:mb-6 text-center" style={{ fontWeight: 400, lineHeight: 1.6 }}>
                  Schedule a free consultation to discuss your project requirements.
                </p>

                <motion.button
                //   onClick={openBookingModal}
                  whileHover={{ y: -2 }}
                  transition={{ duration: 0.16 }}
                  className="w-full inline-flex items-center justify-center rounded-full bg-white px-6 py-3 text-sm sm:text-[15px] font-semibold text-[#0B0B0F] transition-all duration-200 hover:bg-white/90 hover:shadow-lg hover:shadow-white/10 mb-4"
                >
                  Book a Free Consultation
                  <ArrowRight className="ml-2 h-4 w-4" />
                </motion.button>

                <div className="flex items-center justify-center gap-3 sm:gap-4">
                  <button
                    // onClick={openContactModal}
                    className="text-sm sm:text-[14px] text-white/70 hover:text-white transition-colors"
                    style={{ fontWeight: 500 }}
                  >
                    Contact Us
                  </button>
                  <span className="text-white/30">·</span>
                  <a
                    href="mailto:hello@zysoftec.com"
                    className="text-sm sm:text-[14px] text-white/70 hover:text-white transition-colors"
                    style={{ fontWeight: 500 }}
                  >
                    Email Us
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AISolutions;
